/**
 * Administración de usuarios. Solapa propia del portal, exclusiva del rol
 * Administrador.
 *
 * Tres operaciones, las que se definieron el 15-09-2026: crear, activar o
 * inactivar, y restablecer la clave. No hay borrado: un usuario borrado
 * dejaría impactos y auditoría firmados por un nombre que ya no existe, y este
 * módulo escribe en la liquidación.
 *
 * La clave de un alta o de un restablecimiento la elige el administrador y se
 * la entrega a la persona, que tiene que cambiarla en su primer ingreso. Nadie
 * puede ver la clave de nadie: lo guardado es un hash y no se puede revertir.
 */

import { useCallback, useEffect, useState } from 'react';
import { KeyRound, Loader2, UserCheck, UserPlus, UserX } from 'lucide-react';

import { ETIQUETA_ROL, mensajeDe, type RolPortal, type UsuarioDto } from '../../api';
import { Aviso } from '../../components/Aviso';
import { useSesion } from '../../components/useSesion';
import { Modal } from '../../components/Modal';
import { EnvoltorioTabla, FilaVacia } from '../../components/Tabla';
import { fechaHoraLegible } from '../../lib/fechas';
import { crearUsuario, listarUsuarios, modificarUsuario } from '../asistencia/api';

/** Si el back no lo informa, se usa el mismo valor que él trae por defecto. */
const LARGO_MINIMO_POR_DEFECTO = 6;

const ROLES: readonly RolPortal[] = ['rrhh', 'liquidador', 'admin'];

export function VistaUsuarios(): React.JSX.Element {
  // El mínimo lo define el back: acá solo se avisa antes de mandar el pedido.
  const { modulo } = useSesion();
  const largoMinimo = modulo?.largoMinimoClave ?? LARGO_MINIMO_POR_DEFECTO;
  const [usuarios, setUsuarios] = useState<readonly UsuarioDto[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [aviso, setAviso] = useState<string | null>(null);
  const [cargando, setCargando] = useState(true);

  const [alta, setAlta] = useState(false);
  const [reset, setReset] = useState<UsuarioDto | null>(null);

  const cargar = useCallback(async () => {
    setCargando(true);
    try {
      setUsuarios((await listarUsuarios()).usuarios);
      setError(null);
    } catch (err) {
      setError(mensajeDe(err));
    } finally {
      setCargando(false);
    }
  }, []);

  useEffect(() => {
    void cargar();
  }, [cargar]);

  /** Envuelve una operación: refresca la lista y deja el mensaje a la vista. */
  const operar = async (fn: () => Promise<unknown>, exito: string): Promise<boolean> => {
    try {
      await fn();
      await cargar();
      setError(null);
      setAviso(exito);
      return true;
    } catch (err) {
      setAviso(null);
      setError(mensajeDe(err));
      return false;
    }
  };

  return (
    <section className="usuarios">
      {error !== null && <Aviso tono="error">{error}</Aviso>}
      {aviso !== null && <Aviso tono="ok">{aviso}</Aviso>}

      <div className="banda banda--filtros">
        <button type="button" onClick={() => setAlta(true)}>
          <UserPlus size={16} aria-hidden="true" />
          Nuevo usuario
        </button>
        {cargando && <Loader2 className="girando" size={16} aria-hidden="true" />}
      </div>

      <EnvoltorioTabla>
        <table className="tabla tabla--usuarios">
          <thead>
            <tr>
              {/* Nombre y usuario van juntos: son la misma persona, y en dos
                  columnas obligaban a leer de a pares. */}
              <th scope="col">Persona</th>
              <th scope="col">Rol</th>
              <th scope="col">Estado</th>
              <th scope="col" className="num">Último ingreso</th>
              <th scope="col">Acciones</th>
            </tr>
          </thead>
          <tbody>
            {usuarios.length === 0 ? (
              <FilaVacia columnas={5}>
                {cargando ? 'Cargando…' : 'No hay usuarios cargados.'}
              </FilaVacia>
            ) : (
              usuarios.map((u) => (
                <tr key={u.usuario} data-activo={u.activo ? 'si' : 'no'}>
                  <td className="persona">
                    <strong>{u.nombre || u.usuario}</strong>
                    {u.nombre ? <span>{u.usuario}</span> : null}
                  </td>
                  <td>
                    <select
                      aria-label={`Rol de ${u.usuario}`}
                      value={u.rol}
                      onChange={(ev) => {
                        void operar(
                          () => modificarUsuario(u.usuario, { rol: ev.target.value as RolPortal }),
                          `${u.usuario} ahora es ${ETIQUETA_ROL[ev.target.value as RolPortal]}.`,
                        );
                      }}
                    >
                      {ROLES.map((r) => (
                        <option key={r} value={r}>
                          {ETIQUETA_ROL[r]}
                        </option>
                      ))}
                    </select>
                  </td>
                  <td>
                    <span className="pastilla" data-activo={u.activo ? 'si' : 'no'}>
                      {u.activo ? 'Activo' : 'Inactivo'}
                    </span>
                    {u.debeCambiarClave && (
                      <span className="nota"> · clave provisoria</span>
                    )}
                  </td>
                  <td className="num">
                    {u.ultimoIngreso ? (
                      fechaHoraLegible(u.ultimoIngreso, modulo?.zona)
                    ) : (
                      <span className="nota">nunca ingresó</span>
                    )}
                  </td>
                  <td className="acciones">
                    <button
                      type="button"
                      className="plano"
                      onClick={() => {
                        void operar(
                          () => modificarUsuario(u.usuario, { activo: !u.activo }),
                          `${u.usuario} quedó ${u.activo ? 'inactivo' : 'activo'}.`,
                        );
                      }}
                    >
                      {u.activo ? (
                        <UserX size={15} aria-hidden="true" />
                      ) : (
                        <UserCheck size={15} aria-hidden="true" />
                      )}
                      {u.activo ? 'Inactivar' : 'Activar'}
                    </button>
                    <button type="button" className="plano" onClick={() => setReset(u)}>
                      <KeyRound size={15} aria-hidden="true" />
                      Restablecer clave
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </EnvoltorioTabla>

      <p className="nota">
        Los usuarios no se borran: se inactivan. Así la auditoría y los impactos siguen teniendo a
        quién atribuirse.
      </p>

      <DialogoAlta
        abierto={alta}
        largoMinimo={largoMinimo}
        onCerrar={() => setAlta(false)}
        onCrear={async (datos) => {
          const ok = await operar(
            () => crearUsuario(datos),
            `Usuario ${datos.usuario} creado. Entregale la clave: la va a tener que cambiar al ingresar.`,
          );
          if (ok) setAlta(false);
        }}
      />

      <DialogoClave
        usuario={reset}
        largoMinimo={largoMinimo}
        onCerrar={() => setReset(null)}
        onRestablecer={async (claveNueva) => {
          if (!reset) return;
          const ok = await operar(
            () => modificarUsuario(reset.usuario, { claveNueva }),
            `Clave de ${reset.usuario} restablecida. Entregásela: la va a tener que cambiar al ingresar.`,
          );
          if (ok) setReset(null);
        }}
      />
    </section>
  );
}

// --------------------------------------------------------------- diálogos

function DialogoAlta({
  abierto,
  largoMinimo,
  onCerrar,
  onCrear,
}: {
  readonly abierto: boolean;
  readonly largoMinimo: number;
  readonly onCerrar: () => void;
  readonly onCrear: (datos: {
    usuario: string;
    nombre: string;
    rol: RolPortal;
    clave: string;
  }) => Promise<void>;
}): React.JSX.Element {
  const [usuario, setUsuario] = useState('');
  const [nombre, setNombre] = useState('');
  const [rol, setRol] = useState<RolPortal>('rrhh');
  const [clave, setClave] = useState('');

  // Cada vez que se abre, el formulario arranca limpio: si no, la clave del
  // alta anterior quedaría a la vista en el campo.
  useEffect(() => {
    if (abierto) {
      setUsuario('');
      setNombre('');
      setRol('rrhh');
      setClave('');
    }
  }, [abierto]);

  const corta = clave.length > 0 && clave.length < largoMinimo;

  return (
    <Modal abierto={abierto} titulo="Nuevo usuario" onCerrar={onCerrar}>
      <form
        id="alta-usuario"
        className="formulario"
        onSubmit={(ev) => {
          ev.preventDefault();
          void onCrear({ usuario, nombre, rol, clave });
        }}
      >
        <label className="campo">
          Usuario
          <input
            type="text"
            required
            autoComplete="off"
            value={usuario}
            onChange={(ev) => setUsuario(ev.target.value)}
          />
        </label>
        <label className="campo">
          Nombre y apellido
          <input
            type="text"
            autoComplete="off"
            value={nombre}
            onChange={(ev) => setNombre(ev.target.value)}
          />
        </label>
        <label className="campo">
          Rol
          <select value={rol} onChange={(ev) => setRol(ev.target.value as RolPortal)}>
            {ROLES.map((r) => (
              <option key={r} value={r}>
                {ETIQUETA_ROL[r]}
              </option>
            ))}
          </select>
        </label>
        <label className="campo">
          Clave provisoria
          <input
            type="text"
            required
            autoComplete="off"
            minLength={largoMinimo}
            value={clave}
            onChange={(ev) => setClave(ev.target.value)}
          />
        </label>
        {/*
          La clave se muestra en claro a propósito: el administrador la tiene
          que poder leer para dictársela a la persona. Es provisoria y de un
          solo uso.
        */}
        <p className="nota">
          {corta
            ? `Faltan caracteres: el mínimo es ${String(largoMinimo)}.`
            : `Mínimo ${String(largoMinimo)} caracteres. La persona la cambia en su primer ingreso.`}
        </p>
      </form>

      <footer className="modal-pie">
        <button type="button" className="plano" onClick={onCerrar}>
          Cancelar
        </button>
        <button type="submit" form="alta-usuario" disabled={corta}>
          <UserPlus size={15} aria-hidden="true" />
          Crear
        </button>
      </footer>
    </Modal>
  );
}

function DialogoClave({
  usuario,
  largoMinimo,
  onCerrar,
  onRestablecer,
}: {
  readonly usuario: UsuarioDto | null;
  readonly largoMinimo: number;
  readonly onCerrar: () => void;
  readonly onRestablecer: (clave: string) => Promise<void>;
}): React.JSX.Element {
  const [clave, setClave] = useState('');

  useEffect(() => {
    if (usuario) setClave('');
  }, [usuario]);

  const corta = clave.length > 0 && clave.length < largoMinimo;

  return (
    <Modal
      abierto={usuario !== null}
      titulo={`Restablecer la clave de ${usuario?.usuario ?? ''}`}
      onCerrar={onCerrar}
    >
      <form
        id="reset-clave"
        className="formulario"
        onSubmit={(ev) => {
          ev.preventDefault();
          void onRestablecer(clave);
        }}
      >
        <p className="nota">
          La clave guardada no se puede ver ni recuperar. Lo que se hace acá es reemplazarla por
          una nueva, que la persona va a tener que cambiar al ingresar.
        </p>
        <label className="campo">
          Clave nueva
          <input
            type="text"
            required
            autoComplete="off"
            minLength={largoMinimo}
            value={clave}
            onChange={(ev) => setClave(ev.target.value)}
          />
        </label>
      </form>

      <footer className="modal-pie">
        <button type="button" className="plano" onClick={onCerrar}>
          Cancelar
        </button>
        <button type="submit" form="reset-clave" disabled={corta}>
          <KeyRound size={15} aria-hidden="true" />
          Restablecer
        </button>
      </footer>
    </Modal>
  );
}
